/**
 * ドラッグ&ドロップ共通フック
 * ChatArea と ImageGenerator で共有
 */

import { useState, useRef, useCallback, useEffect, DragEvent } from 'react';
import type { FileAttachment as FileAttachmentType } from '../types';
import {
  filesToAttachments,
  DEFAULT_MAX_FILES,
  DEFAULT_ACCEPTED_TYPES,
} from '../services/fileService';

export interface UseDragAndDropOptions {
  /** 受け付けるファイルタイプ (MIMEタイプ) */
  acceptedTypes?: string[];
  /** 最大ファイル数 */
  maxFiles?: number;
  /** 現在添付済みのファイル数 */
  currentFileCount?: number;
  /** エラー表示時間 (ms) */
  errorDisplayDuration?: number;
  /** ドロップ前のカスタムフィルター (例: 画像のみ) */
  fileFilter?: (file: File) => boolean;
  /** フィルターエラーメッセージ */
  filterErrorMessage?: string;

  /** false にするとドロップを受け付けず preventDefault のみ行う */
  enabled?: boolean;
}

export interface UseDragAndDropReturn {
  /** ドラッグ中かどうか */
  isDragging: boolean;
  /** ドロップされたファイル */
  droppedFiles: FileAttachmentType[];
  /** ドロップエラーメッセージ */
  dropError: string;
  /** ドラッグイベントハンドラ */
  handlers: {
    onDragEnter: (e: DragEvent<HTMLElement>) => void;
    onDragLeave: (e: DragEvent<HTMLElement>) => void;
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
  };
  /** ドロップファイルをクリア */
  clearDroppedFiles: () => void;
  /** エラーをクリア */
  clearDropError: () => void;
  /** エラーを設定（一定時間後に自動クリア） */
  setDropError: (error: string) => void;
}

export function useDragAndDrop(options: UseDragAndDropOptions = {}): UseDragAndDropReturn {
  const {
    acceptedTypes = DEFAULT_ACCEPTED_TYPES,
    maxFiles = DEFAULT_MAX_FILES,
    currentFileCount = 0,
    errorDisplayDuration = 3000,
    fileFilter,
    filterErrorMessage,
    enabled = true,
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<FileAttachmentType[]>([]);
  const [dropError, setDropError] = useState<string>('');
  const dragCounterRef = useRef(0);

  const clearDroppedFiles = useCallback(() => {
    setDroppedFiles([]);
  }, []);

  const clearDropError = useCallback(() => {
    setDropError('');
  }, []);

  // エラーが設定されたら一定時間後に自動クリア（メモリリーク防止のためuseEffectで管理）
  useEffect(() => {
    if (dropError) {
      const timer = setTimeout(() => {
        setDropError('');
      }, errorDisplayDuration);

      // クリーンアップ: コンポーネントアンマウント時またはdropError変更時にタイマークリア
      return () => clearTimeout(timer);
    }
  }, [dropError, errorDisplayDuration]);

  const handleDragEnter = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!enabled) return;
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
      setDropError('');
    }
  }, [enabled]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!enabled) return;
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, [enabled]);

  const handleDragOver = useCallback((e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!enabled) return;
      setIsDragging(false);
      dragCounterRef.current = 0;

      let files: File[] = Array.from(e.dataTransfer.files);
      if (files.length === 0) return;

      // カスタムフィルターがある場合は適用
      if (fileFilter) {
        const filteredFiles = files.filter(fileFilter);
        if (filteredFiles.length === 0) {
          if (filterErrorMessage) {
            setDropError(filterErrorMessage);
          }
          return;
        }
        files = filteredFiles;
      }

      // ファイルを検証・変換
      const result = await filesToAttachments(
        files,
        undefined,
        acceptedTypes,
        maxFiles,
        currentFileCount
      );

      if (result.error) {
        setDropError(result.error);
        return;
      }

      if (result.attachments.length > 0) {
        setDroppedFiles(result.attachments);
      }
    },
    [enabled, acceptedTypes, maxFiles, currentFileCount, fileFilter, filterErrorMessage]
  );

  return {
    isDragging,
    droppedFiles,
    dropError,
    handlers: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
    clearDroppedFiles,
    clearDropError,
    setDropError,
  };
}
